// bluaIQ — Read-only banner (PR2, decision Blu 2026-10-22)
//
// An account without a paid subscription (cancelled, incomplete, or trial
// over) can still open and download its projects, but cannot create or
// edit. The SERVER enforces it (402 SUBSCRIPTION_READ_ONLY from
// requireWritableSubscription); this bar only explains it and links to
// Membership. Shown when /api/subscription says access.mode = read_only, or
// as soon as any write comes back 402 SUBSCRIPTION_READ_ONLY.
//
// Fixed to the bottom of the viewport so it never shifts any layout.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { useSubscriptionPlan } from '../hooks/useSubscriptionPlan'
import { READ_ONLY_EVENT } from '../utils/api'

export function readOnlyMessage(reason: string | null | undefined, isEs: boolean): string {
  if (reason === 'trial_expired') {
    return isEs
      ? 'Tu prueba gratis terminó. Tus proyectos están en modo solo lectura: puedes verlos y descargarlos, pero no editarlos.'
      : 'Your free trial has ended. Your projects are read-only: you can view and download them, but not edit.'
  }
  return isEs
    ? 'Tu suscripción no está activa. Tus proyectos están en modo solo lectura: puedes verlos y descargarlos, pero no editarlos.'
    : 'Your subscription is not active. Your projects are read-only: you can view and download them, but not edit.'
}

export default function ReadOnlyBanner() {
  const { i18n } = useTranslation()
  const isEs = (i18n.language || 'es').startsWith('es')
  const navigate = useNavigate()
  const { accessMode, accessReason } = useSubscriptionPlan()
  const [eventReason, setEventReason] = useState<string | null | undefined>(undefined)

  useEffect(() => {
    const onReadOnly = (e: Event) => {
      const detail = (e as CustomEvent<{ reason?: string | null }>).detail
      setEventReason(detail?.reason ?? null)
    }
    window.addEventListener(READ_ONLY_EVENT, onReadOnly)
    return () => window.removeEventListener(READ_ONLY_EVENT, onReadOnly)
  }, [])

  const visible = accessMode === 'read_only' || eventReason !== undefined
  if (!visible) return null
  const reason = accessReason ?? eventReason ?? null

  return (
    <div
      role="status"
      data-testid="read-only-banner"
      className="fixed bottom-0 inset-x-0 z-[70] bg-[#06539B] text-white px-4 py-2.5 flex items-center gap-3 shadow-lg"
    >
      <Lock size={16} className="flex-shrink-0" />
      <span className="flex-1 min-w-0 text-[12px] font-[700]">{readOnlyMessage(reason, isEs)}</span>
      <button
        onClick={() => navigate('/subscription')}
        className="bg-[#00BCD4] text-[#06539B] text-[13px] font-[800] px-4 py-1.5 rounded-full hover:opacity-90 transition-opacity flex-shrink-0"
      >
        {isEs ? 'Activar plan' : 'Activate plan'}
      </button>
    </div>
  )
}
